import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import {
  requestClearFantasyAnswer,
  requestFantasyAnswers,
  requestSetFantasyAnswer,
  toFantasyMatchError,
} from '../../services/fantasyMatch';
import type {
  FantasyAnswersPayload,
  FantasyAnswerValue,
  FantasyMatchError,
} from '../../types/fantasyMatch';
import { logoutUser } from './authSlice';

interface PendingAnswer {
  /** `null`: the answer is being cleared. */
  answer: FantasyAnswerValue | null;
  requestId: string;
}

export interface FantasyMatchState {
  /** The last payload the server sent. Optimistic taps live in `pending`. */
  payload: FantasyAnswersPayload | null;
  /** `idle` until the first GET settles; refreshes never flip it back. */
  status: 'idle' | 'loading' | 'ready' | 'failed';
  /** Taps the server has not confirmed yet, by item slug. */
  pending: Record<string, PendingAnswer>;
}

const initialState: FantasyMatchState = {
  payload: null,
  status: 'idle',
  pending: {},
};

/** GET /fantasy/answers. Also used for the realtime refresh. */
export const fetchFantasyAnswers = createAsyncThunk<
  FantasyAnswersPayload,
  void,
  { rejectValue: FantasyMatchError }
>('fantasyMatch/fetch', async (_, { rejectWithValue }) => {
  try {
    return await requestFantasyAnswers();
  } catch (error) {
    return rejectWithValue(toFantasyMatchError(error));
  }
});

/** Saves (or, with `answer: null`, clears) my answer to one item. */
export const saveFantasyAnswer = createAsyncThunk<
  FantasyAnswersPayload,
  { slug: string; answer: FantasyAnswerValue | null },
  { rejectValue: FantasyMatchError }
>('fantasyMatch/save', async ({ slug, answer }, { rejectWithValue }) => {
  try {
    return answer === null
      ? await requestClearFantasyAnswer(slug)
      : await requestSetFantasyAnswer(slug, answer);
  } catch (error) {
    return rejectWithValue(toFantasyMatchError(error));
  }
});

const fantasyMatchSlice = createSlice({
  name: 'fantasyMatch',
  initialState,
  reducers: {
    resetFantasyMatch: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFantasyAnswers.pending, state => {
        if (state.status === 'idle' || state.status === 'failed') {
          state.status = 'loading';
        }
      })
      .addCase(fetchFantasyAnswers.fulfilled, (state, action) => {
        state.payload = action.payload;
        state.status = 'ready';
      })
      .addCase(fetchFantasyAnswers.rejected, state => {
        if (state.payload === null) state.status = 'failed';
      })
      .addCase(saveFantasyAnswer.pending, (state, action) => {
        state.pending[action.meta.arg.slug] = {
          answer: action.meta.arg.answer,
          requestId: action.meta.requestId,
        };
      })
      .addCase(saveFantasyAnswer.fulfilled, (state, action) => {
        state.payload = action.payload;
        state.status = 'ready';
        const entry = state.pending[action.meta.arg.slug];
        if (entry?.requestId === action.meta.requestId) {
          delete state.pending[action.meta.arg.slug];
        }
      })
      .addCase(saveFantasyAnswer.rejected, (state, action) => {
        // Dropping the optimistic entry puts the last confirmed answer back.
        const entry = state.pending[action.meta.arg.slug];
        if (entry?.requestId === action.meta.requestId) {
          delete state.pending[action.meta.arg.slug];
        }
      })
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { resetFantasyMatch } = fantasyMatchSlice.actions;

type WithFantasyMatch = { fantasyMatch: FantasyMatchState };

/** My answers with the taps that are still being saved laid over them. */
export const selectMyFantasyAnswers = createSelector(
  [
    (state: WithFantasyMatch) => state.fantasyMatch.payload,
    (state: WithFantasyMatch) => state.fantasyMatch.pending,
  ],
  (payload, pending): Record<string, FantasyAnswerValue> => {
    const mine = { ...(payload?.mine ?? {}) };
    for (const [slug, entry] of Object.entries(pending)) {
      if (entry.answer === null) {
        delete mine[slug];
      } else {
        mine[slug] = entry.answer;
      }
    }
    return mine;
  }
);

export default fantasyMatchSlice.reducer;
